<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useCartStore } from '~/store/CartStore'
import { useMainStore } from '~/store/MainStore'
import CartItem from '~/components/Cart/CartItem.vue'
import { calcSubtotal } from '~/classes/CartUtils'
import type { BpCartItem } from '~/definitions/AllDefinitions'

const { t } = useI18n()
const cartStore = useCartStore()
const { currencyCode, isOrderingEnabled } = storeToRefs(useMainStore())
const { cartItems } = storeToRefs(cartStore)
defineProps<{
  open: boolean
}>()
function closeDialog() {
  cartStore.setCartIsOpen(false)
}
const subTotal = computed(() => {
  return calcSubtotal(cartStore.items)
})

async function handleRemoveCartItem(uuid: string) {
  const idx = cartItems.value.findIndex((item: BpCartItem) => item.uuid === uuid)
  if (idx > -1) {
    cartItems.value.splice(idx, 1)
  } else {
    await cartStore.getCart()
  }
}
</script>

<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="relative z-40"
      @close="closeDialog"
    >
      <TransitionChild
        as="template"
        enter="ease-in-out duration-500"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in-out duration-500"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-hidden">
        <div class="absolute inset-0 overflow-hidden">
          <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel class="pointer-events-auto w-screen max-w-md">
                <div class="flex h-full flex-col overflow-y-scroll bg-tw-1 shadow-xl">
                  <div class="flex-1 overflow-y-auto py-6">
                    <div class="flex items-start justify-between px-4 sm:px-6">
                      <DialogTitle class="text-lg font-medium text-tw-1">
                        {{ t('SHOPPING_CART') }}
                      </DialogTitle>
                      <div class="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          class="relative -m-2 p-2 text-gray-400 hover:text-gray-500"
                          @click="closeDialog()"
                        >
                          <span class="absolute -inset-0.5" />
                          <span class="sr-only">{{ t('CLOSE_PANEL') }}</span>
                          <XMarkIcon
                            class="h-6 w-6"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </div>

                    <div
                      v-if="cartItems && cartItems.length"
                      class="mt-8"
                    >
                      <div class="flow-root">
                        <ul
                          role="list"
                          class="-my-2"
                        >
                          <cart-item
                            v-for="item in cartItems"
                            :key="item.uuid"
                            :disabled="!isOrderingEnabled"
                            class="border-t border-tw-2"
                            :item="item"
                            @remove-cart-item="handleRemoveCartItem"
                          />
                        </ul>
                      </div>
                    </div>
                    <div
                      v-else
                      class="mt-5 pt-5 text-tw-1 sm:px-6 px-4 border-t border-tw-2"
                    >
                      {{ t('EMPTY_CART') }}
                    </div>
                    <div
                      v-if="!isOrderingEnabled"
                      class="px-5 mt-5"
                    >
                      <tw-alert
                        :headline="t('STORE_IS_CURRENTLY_CLOSED')"
                        type="error"
                        :sticky="false"
                        :disable-dismiss="true"
                      />
                    </div>
                  </div>

                  <div class="border-t border-tw-2 px-4 py-6 sm:px-6">
                    <div class="flex justify-between text-base font-medium text-tw-1">
                      <p>{{ t('SUBTOTAL') }}</p>
                      <p>{{ $filters.formatCurrency(subTotal, currencyCode) }}</p>
                    </div>
                    <p class="mt-0.5 text-sm text-tw-2">
                      {{ t('SHIPPING_CALCULATED_CHECKOUT') }}
                    </p>
                    <div class="mt-5">
                      <router-link
                        v-if="cartItems.length && isOrderingEnabled"
                        to="/checkout"
                        class="tw-button primary"
                      >
                        {{ t('CHECKOUT') }}
                      </router-link>
                      <div
                        v-else
                        class="tw-button opacity-40 primary"
                      >
                        {{ t('CHECKOUT') }}
                      </div>
                    </div>
                    <div class="mt-6 flex justify-center text-center text-sm text-tw-2">
                      <p>
                        {{ t('OR').toLowerCase() }}
                        <button
                          type="button"
                          class="tw-text-button"
                          @click="closeDialog()"
                        >
                          {{ t('CONTINUE_SHOPPING') }}
                          <span aria-hidden="true"> &rarr;</span>
                        </button>
                      </p>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
